import { getBrowserStorage } from "./browserStorage";

export type FeedbackPromptRunThreshold = 1 | 3;

export type FeedbackPromptState = {
  completedRuns: number;
  promptedThreshold: number;
  hasSubmitted: boolean;
};

const STORAGE_KEY = "tavela.feedbackPrompt";

const DEFAULT_FEEDBACK_PROMPT_STATE: FeedbackPromptState = {
  completedRuns: 0,
  promptedThreshold: 0,
  hasSubmitted: false
};

function normalizeNumber(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.trunc(value));
}

export function normalizeFeedbackPromptState(value: unknown): FeedbackPromptState {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_FEEDBACK_PROMPT_STATE;
  }

  const record = value as Partial<Record<keyof FeedbackPromptState, unknown>>;

  return {
    completedRuns: normalizeNumber(record.completedRuns),
    promptedThreshold: normalizeNumber(record.promptedThreshold),
    hasSubmitted: record.hasSubmitted === true
  };
}

export function loadFeedbackPromptState(): FeedbackPromptState {
  const storage = getBrowserStorage();
  if (storage === null) {
    return DEFAULT_FEEDBACK_PROMPT_STATE;
  }

  try {
    const storedValue = storage.getItem(STORAGE_KEY);
    if (storedValue === null) {
      return DEFAULT_FEEDBACK_PROMPT_STATE;
    }

    return normalizeFeedbackPromptState(JSON.parse(storedValue));
  } catch {
    return DEFAULT_FEEDBACK_PROMPT_STATE;
  }
}

export function saveFeedbackPromptState(value: FeedbackPromptState): FeedbackPromptState {
  const nextState = normalizeFeedbackPromptState(value);
  const storage = getBrowserStorage();
  if (storage !== null) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(nextState));
    } catch {
      // Ignore storage failures so prompts can still work in-memory.
    }
  }

  return nextState;
}

export function recordCompletedRun(value: FeedbackPromptState): FeedbackPromptState {
  return normalizeFeedbackPromptState({
    ...value,
    completedRuns: value.completedRuns + 1
  });
}

export function getPendingFeedbackPromptThreshold(
  value: FeedbackPromptState
): FeedbackPromptRunThreshold | null {
  if (value.hasSubmitted) {
    return null;
  }

  if (value.completedRuns >= 3 && value.promptedThreshold < 3) {
    return 3;
  }

  if (value.completedRuns >= 1 && value.promptedThreshold < 1) {
    return 1;
  }

  return null;
}

export function markFeedbackPromptShown(
  value: FeedbackPromptState,
  threshold: FeedbackPromptRunThreshold
): FeedbackPromptState {
  return normalizeFeedbackPromptState({
    ...value,
    promptedThreshold: Math.max(value.promptedThreshold, threshold)
  });
}

export function markFeedbackSubmitted(value: FeedbackPromptState): FeedbackPromptState {
  return normalizeFeedbackPromptState({
    ...value,
    hasSubmitted: true
  });
}
